import { useDispatch } from "react-redux";
import React, { useRef } from "react";
import styled from "styled-components";
import { useAppSelector } from "src/redux/store";
import { setSelectedCard } from "src/redux/features/custom-board/custom-board-slice";
import { clientPopoverOptions } from "../../constants/client";
import { providerPopoverOptions } from "../../constants/providers";
import ProviderImg from "../../assets/images/images-svg/provider-large-vertical.svg";
import ClientImg from "../../assets/images/images-svg/client-large.svg";
import {
  getBoardAgingDays,
  getBoardsUpdatedDays,
  getTaskOverallDays,
} from "src/helpers/reverse-list/reverse-list";
import { Draggable, DraggableProvided, DraggableStateSnapshot } from "@hello-pangea/dnd";
import { setDraggingAtomState } from "src/redux/features/trello-board/dragging-state-slice";
import PopoverOptions from "./PopoverOptions";
import { ReactComponent as Dots } from "../../assets/images/images-svg/dots.svg";
import { Popover } from "@mui/material";

interface ITaskComponenetProps {
  id: string;
  index: number;
  task: any; // Replace with actual task type
  stageId: string;
  stageName: string;
  intakeOrOnboarding: boolean;
  callbacks: any;
  chatCommand: (clientId: string) => void;
}

const Wrapper = styled.div<{ isDragging: boolean }>`
  width: 100%;
  padding: 10px 22px 10px 22px;
  border-radius: 5px;
  align-items: center;
  cursor: ${(props) => (props.isDragging ? "grabbing" : "grab")};
  margin-bottom: 8px;
  transition: all 0.2s ease;

  box-shadow: ${(props) =>
    props.isDragging
      ? "0 4px 8px rgba(0, 123, 255, 0.2)"
      : "0 1px 3px rgba(0, 0, 0, 0.1)"};
  transform: ${(props) => (props.isDragging ? "scale(1.02)" : "scale(1)")};

  &:hover {
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
  }

  &:active {
    cursor: grabbing;
  }
`;

const Task: React.FC<ITaskComponenetProps> = ({
  id,
  index,
  task,
  stageName,
  stageId,
  intakeOrOnboarding,
  callbacks,
  chatCommand,
}) => {
  const dispatch = useDispatch();
  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(
    null
  );
  const open = Boolean(anchorEl);
  const prevDraggingRef = useRef(false);

  const selectedCard = useAppSelector<{ cardId: string; cardTitle: string }[]>(
    (state) => state.boardSlice.selectedCard
  );

  const selectHandler = (e: any) => {
    e.stopPropagation();
    if (selectedCard.some((item) => item.cardId === id)) {
      dispatch(
        setSelectedCard(selectedCard.filter((card) => card.cardId !== id))
      );
    } else {
      dispatch(
        setSelectedCard([
          ...selectedCard,
          { cardId: id, cardTitle: task.cardTitle },
        ])
      );
    }
  };

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <Draggable draggableId={task.id} index={index}>
      {(provided: DraggableProvided, snapshot: DraggableStateSnapshot) => {
        // Handle drag state changes
        if (snapshot.isDragging !== prevDraggingRef.current) {
          prevDraggingRef.current = snapshot.isDragging;
          if (snapshot.isDragging) {
            dispatch(setDraggingAtomState(true));
          } else {
            dispatch(setDraggingAtomState(false));
          }
        }

        return (
          <Wrapper
            ref={provided.innerRef}
            {...provided.draggableProps}
            {...provided.dragHandleProps}
            isDragging={snapshot.isDragging}
            className={
              selectedCard.some((item) => item.cardId === id)
                ? "StageInnerCard selected"
                : "StageInnerCard"
            }
            onClick={selectHandler}
          >
            <div className="StageInnerCard__user">
              <div className="StageInnerCard__userInner">
                <span className="StageInnerCard__userImage">
                  <img
                    src={
                      task.entityType === "Client"
                        ? task.cardProfile.url
                          ? task.cardProfile.url
                          : ClientImg
                        : task.cardProfile.url
                        ? task.cardProfile.url
                        : ProviderImg
                    }
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.onerror = null; // Prevent infinite loop
                      if (task.entityType === "Client") {
                        target.src = ClientImg;
                      } else {
                        target.src = ProviderImg;
                      }
                    }}
                    alt="client"
                    className="img-fluid"
                    onClick={(e) => {
                      e.stopPropagation();
                      chatCommand(task.entityId);
                    }}
                  />
                </span>
                <div className="StageInnerCard__userInfo">
                  <div>
                    <h6 className="StageInnerCard__userName">
                      {task.cardTitle}
                    </h6>
                  </div>
                  {task.boardColumnUpdatedAt && (
                    <p>{getBoardsUpdatedDays(task.boardColumnUpdatedAt)}</p>
                  )}
                </div>
              </div>
              <div style={{ flex: 1 }} className="StageInnerCard__info">
                {task.createdAt && (
                  <h5 className="StageInnerCard__time">
                    {
                      stageName?.toUpperCase() === "INTAKE COMPLETE" ||
                      stageName?.toUpperCase() === "CREDENTIALING: COMPLETE"
                        ? getTaskOverallDays(
                            task.createdAt,
                            task.boardColumnUpdatedAt
                          )
                        : getBoardAgingDays(task.createdAt)
                    }
                  </h5>
                )}
                {intakeOrOnboarding && (
                  <div>
                    <button
                      aria-describedby={id}
                      onClick={handleClick}
                      style={{ cursor: "pointer" }}
                    >
                      <Dots />
                    </button>
                    <Popover
                      id={id}
                      open={open}
                      anchorEl={anchorEl}
                      onClose={handleClose}
                      anchorOrigin={{
                        vertical: "bottom",
                        horizontal: "center",
                      }}
                      transformOrigin={{
                        vertical: "top",
                        horizontal: "center",
                      }}
                    >
                      <ul className="popoverLinkList">
                        {task.entityType === "Client"
                          ? clientPopoverOptions.map(
                              (item: string, index: number) => (
                                <PopoverOptions
                                  optionName={item}
                                  key={index}
                                  callback={(id: string) => callbacks[0][index](id)}
                                  entityId={task.entityId}
                                  taskId={task.id}
                                  taskType={task.entityType}
                                  closeAnchor={() => handleClose()}
                                />
                              )
                            )
                          : providerPopoverOptions.map(
                              (item: string, index: number) => (
                                <PopoverOptions
                                  optionName={item}
                                  key={index}
                                  callback={(id: string) => callbacks[1][index](id)}
                                  taskId={task.id}
                                  taskType={task.entityType}
                                  entityId={task.entityId}
                                  closeAnchor={() => handleClose()}
                                />
                              )
                            )}
                      </ul>
                    </Popover>
                  </div>
                )}
              </div>
            </div>
          </Wrapper>
        );
      }}
    </Draggable>
  );
};

export default React.memo(Task);
